// src/components/public_utilities/AiAssetHelper.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — AI Asset Helper
//
// Tells the user, in plain language, what information FlowSense needs for an
// asset, why each piece matters, which AI features it unlocks, and what the AI
// currently concludes about the asset.
//
//   <AiAssetHelper asset context="form|edit|view" variant="full|compact" onFix />
//   <AssetFieldHint field="elevation" />   or   <AssetFieldHint metric="health" />
//   <AssetHealthCard analysis onAction />  health index, life, maintenance, drivers, actions
//   <AssetAiCell asset onEdit />           compact table cell with a popover helper
//   <PredictionExplainer prediction />     "Why?" popover for Predictive Maintenance rows
//
// Logic lives in assetAiGuide.js; this file is presentation only.
//
// --- UPDATE (Public Utilities v3)
//   • Public Utilities identity row: asset code (DW-BAM-0001), constituency (= zone) and who manages it —
//     "No manager" / "Not in a constituency" are flagged because work cannot be routed without them.
//     (Shown only; the readiness score from assetAiGuide is unchanged.)
//   • Ask FlowSense about this asset (`enableAsk`, default on for context="view"): the question goes to
//     /flowsense-ai/ask with this asset's readiness, missing data, insights and identity as context; if
//     the AI is offline the helper's own summary and top missing items are shown instead.
// --- END UPDATE
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo, useMemo, useState, useCallback } from 'react'; // --- UPDATE (v3): + useCallback
import {
    Badge, Box, Button, CircularProgress, CircularProgressLabel, Collapse, Flex, HStack, Icon,
    IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal,
    Progress, SimpleGrid, Text, Tooltip, VStack, useColorModeValue,
} from '@chakra-ui/react';
import {
    MdOutlineAutoAwesome, MdCheckCircle, MdWarning, MdErrorOutline, MdHelpOutline,
    MdOutlineTipsAndUpdates, MdArrowForward, MdExpandMore, MdExpandLess, MdPsychology,
    MdSensors, MdOutlineWaterDrop, MdAttachMoney, MdMap, MdSupportAgent, MdTimeline,
    MdBuild, MdEdit, MdVisibility, MdReportProblem, MdSchedule, MdHealthAndSafety,
} from 'react-icons/md';
import {
    ASSET_AI_MODELS, ASSET_FIELD_GUIDE, ASSET_METRIC_GUIDE, analyzeAsset, getAssetAnalysis, explainPrediction,
} from './assetAiGuide';
// --- UPDATE (v3)
import { Input, InputGroup, InputRightElement, Spinner } from '@chakra-ui/react';
import { MdSend } from 'react-icons/md';
import { askPublicUtilitiesAi } from '../../services/publicUtilitiesApi';
// --- END UPDATE

const MODEL_ICON = {
    failure: MdTimeline, anomaly: MdSensors, hydraulic: MdOutlineWaterDrop,
    lifecycle: MdAttachMoney, gis: MdMap, dispatch: MdSupportAgent,
};
const STATUS_ICON = { ok: MdCheckCircle, warning: MdWarning, missing: MdErrorOutline };
const STATUS_COLOR = { ok: 'green.400', warning: 'orange.400', missing: 'red.400' };
const LEVEL_SCHEME = { required: 'red', recommended: 'purple', optional: 'gray' };
const MODEL_STATE = { ready: { label: 'Ready', scheme: 'green' }, partial: { label: 'Partial', scheme: 'yellow' }, blocked: { label: 'Blocked', scheme: 'red' } };
const INSIGHT_STYLE = {
    critical: { icon: MdErrorOutline, color: 'red.400' },
    high: { icon: MdWarning, color: 'orange.400' },
    warning: { icon: MdWarning, color: 'orange.400' },
    info: { icon: MdOutlineTipsAndUpdates, color: 'blue.400' },
    ok: { icon: MdCheckCircle, color: 'green.400' },
    success: { icon: MdCheckCircle, color: 'green.400' },
};
const PRIORITY_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray' };
const INTENT_ICON = { SCHEDULE: MdSchedule, REPORT_FAILURE: MdReportProblem, LOG_MAINTENANCE: MdBuild, EDIT: MdEdit, VIEW: MdVisibility };
const INTENT_LABEL = { SCHEDULE: 'Schedule', REPORT_FAILURE: 'Report', LOG_MAINTENANCE: 'Log', EDIT: 'Add', VIEW: 'Open' };

// ─────────────────────────────────────────────────────────────────────────────
// Hint popover
// ─────────────────────────────────────────────────────────────────────────────
export const AssetFieldHint = memo(({ field, metric, placement = 'top' }) => {
    const g = field ? ASSET_FIELD_GUIDE[field] : ASSET_METRIC_GUIDE[metric];
    const bg = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.300');
    if (!g) return null;
    return (
        <Popover trigger="hover" placement={placement} isLazy openDelay={150}>
            <PopoverTrigger>
                <IconButton
                    as="span" role="button" tabIndex={0}
                    aria-label={`What is ${g.label}?`} icon={<Icon as={MdHelpOutline} />}
                    size="xs" variant="ghost" minW="auto" h="auto" p={0.5} ml={1} color="purple.400"
                    verticalAlign="middle" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                />
            </PopoverTrigger>
            <Portal>
                <PopoverContent bg={bg} w="300px" borderRadius="xl" boxShadow="xl" textTransform="none" letterSpacing="normal" fontWeight="normal">
                    <PopoverArrow bg={bg} />
                    <PopoverBody p={3}>
                        <HStack spacing={1.5} mb={1}>
                            <Icon as={MdOutlineAutoAwesome} color="purple.400" />
                            <Text fontWeight="800" fontSize="sm">{g.label}{g.unit ? ` (${g.unit})` : ''}</Text>
                            {g.level && <Badge colorScheme={LEVEL_SCHEME[g.level]} fontSize="9px">{g.level}</Badge>}
                        </HStack>
                        <Text fontSize="xs" color={muted} mb={2}>{g.explain}</Text>
                        {g.why && <Text fontSize="xs" mb={2}><b>Why FlowSense needs it:</b> {g.why}</Text>}
                        {g.scale && <Text fontSize="xs" mb={2}><b>Scale:</b> {g.scale}</Text>}
                        {g.example && <Text fontSize="xs" color={muted}>Example: <Text as="span" fontFamily="mono">{g.example}</Text></Text>}
                        {g.usedBy?.length > 0 && (
                            <HStack spacing={1} mt={2} flexWrap="wrap">
                                {g.usedBy.map(k => <Badge key={k} colorScheme={ASSET_AI_MODELS[k].color} fontSize="9px" variant="subtle">{ASSET_AI_MODELS[k].short}</Badge>)}
                            </HStack>
                        )}
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
});
AssetFieldHint.displayName = 'AssetFieldHint';

// ─────────────────────────────────────────────────────────────────────────────
// Small pieces
// ─────────────────────────────────────────────────────────────────────────────
const ReadinessRing = memo(({ score, grade, size = '60px' }) => {
    const track = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
    return (
        <Tooltip label={ASSET_METRIC_GUIDE.readiness.explain} hasArrow>
            <Box flexShrink={0}>
                <CircularProgress value={score} color={`${grade.color}.400`} size={size} thickness="10px" trackColor={track}>
                    <CircularProgressLabel fontSize="sm" fontWeight="900">{score}%</CircularProgressLabel>
                </CircularProgress>
            </Box>
        </Tooltip>
    );
});
ReadinessRing.displayName = 'ReadinessRing';

const CheckRow = memo(({ c, onFix, expanded, onToggle }) => {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
    const canFix = onFix && c.status !== 'ok' && c.canFix;
    return (
        <Box borderRadius="md" _hover={{ bg: rowHover }}>
            <Flex align="center" gap={2} px={2} py={1.5} cursor="pointer" onClick={onToggle} role="button" aria-expanded={expanded}
                aria-label={`${c.label}: ${c.status}`}>
                <Icon as={STATUS_ICON[c.status]} color={STATUS_COLOR[c.status]} flexShrink={0} />
                <Text fontSize="xs" fontWeight="700" flex={1} noOfLines={1}>{c.label}</Text>
                <Badge colorScheme={LEVEL_SCHEME[c.level]} fontSize="9px" variant="subtle">{c.level}</Badge>
                {canFix && (
                    <Button size="xs" variant="ghost" colorScheme="purple" rightIcon={<Icon as={MdArrowForward} />}
                        onClick={(e) => { e.stopPropagation(); onFix(c.id, c); }}>
                        {c.status === 'warning' ? 'Check' : 'Add'}
                    </Button>
                )}
                <Icon as={expanded ? MdExpandLess : MdExpandMore} color={muted} />
            </Flex>
            <Collapse in={expanded} animateOpacity>
                <Box px={8} pb={2}>
                    {c.note && <Text fontSize="xs" color="orange.400" fontWeight="600" mb={1}>{c.note}</Text>}
                    <Text fontSize="xs" color={muted} mb={1}>{c.explain}</Text>
                    <Text fontSize="xs"><b>Why:</b> {c.why}</Text>
                    {c.usedBy.length > 0 && (
                        <HStack spacing={1} mt={1.5} flexWrap="wrap">
                            {c.usedBy.map(k => <Badge key={k} colorScheme={ASSET_AI_MODELS[k].color} fontSize="9px">{ASSET_AI_MODELS[k].short}</Badge>)}
                        </HStack>
                    )}
                    {!c.canFix && c.status !== 'ok' && c.id === 'sensors' && (
                        <Text fontSize="2xs" color={muted} mt={1}>Link sensors from Sensor Management.</Text>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
});
CheckRow.displayName = 'CheckRow';

const fmtDate = (d) => (d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

// ─────────────────────────────────────────────────────────────────────────────
// Health card — what the AI concludes about the asset
// ─────────────────────────────────────────────────────────────────────────────
export const AssetHealthCard = memo(({ analysis, onAction }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const subtle = useColorModeValue('gray.50', 'whiteAlpha.50');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    if (!analysis) return null;
    const a = analysis;
    return (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} role="region" aria-label="AI asset health">
            <Flex gap={4} wrap="wrap" align="flex-start">
                <HStack spacing={3} minW="180px">
                    <CircularProgress value={a.health} color={`${a.healthLevel.color}.400`} size="64px" thickness="10px" trackColor={track}>
                        <CircularProgressLabel fontSize="md" fontWeight="900">{a.health}</CircularProgressLabel>
                    </CircularProgress>
                    <Box>
                        <HStack spacing={0}>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted}>Health index</Text>
                            <AssetFieldHint metric="health" />
                        </HStack>
                        <Badge colorScheme={a.healthLevel.color} fontSize="sm">{a.healthLevel.label}</Badge>
                        <Text fontSize="2xs" color={muted} mt={1}>{a.familyLabel}</Text>
                    </Box>
                </HStack>

                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} flex={1} minW="260px">
                    <Box>
                        <HStack spacing={0}><Text fontSize="2xs" color={muted} fontWeight="700" textTransform="uppercase">Design life used</Text><AssetFieldHint metric="life_used" /></HStack>
                        {a.lifeUsed != null ? (
                            <>
                                <Text fontSize="sm" fontWeight="800">{Math.round(a.lifeUsed * 100)}%</Text>
                                <Progress value={Math.min(100, a.lifeUsed * 100)} size="xs" borderRadius="full" bg={track}
                                    colorScheme={a.lifeUsed >= 1 ? 'red' : a.lifeUsed >= 0.8 ? 'orange' : 'green'} />
                                <Text fontSize="2xs" color={muted}>{a.ageYears.toFixed(1)} of {a.lifespan} yrs</Text>
                            </>
                        ) : <Text fontSize="xs" color="orange.400" fontWeight="700">Install date missing</Text>}
                    </Box>
                    <Box>
                        <Text fontSize="2xs" color={muted} fontWeight="700" textTransform="uppercase">Next maintenance</Text>
                        <Text fontSize="sm" fontWeight="800" color={a.maintOverdueDays > 0 ? 'red.400' : undefined}>{fmtDate(a.nextMaintenance)}</Text>
                        {a.maintOverdueDays != null && (
                            <Text fontSize="2xs" color={a.maintOverdueDays > 0 ? 'red.400' : muted}>
                                {a.maintOverdueDays > 0 ? `${a.maintOverdueDays}d overdue` : `in ${Math.abs(a.maintOverdueDays)}d`}
                            </Text>
                        )}
                    </Box>
                    <Box>
                        <HStack spacing={0}><Text fontSize="2xs" color={muted} fontWeight="700" textTransform="uppercase">Failure risk</Text><AssetFieldHint metric="risk" /></HStack>
                        {a.model.riskScore != null ? (
                            <HStack spacing={1}>
                                <Text fontSize="sm" fontWeight="800">{a.model.riskScore}/100</Text>
                                {a.model.confidence != null && <Badge fontSize="9px">{a.model.confidence}% conf.</Badge>}
                            </HStack>
                        ) : <Text fontSize="xs" color={muted}>No ML score yet</Text>}
                    </Box>
                    <Box>
                        <Text fontSize="2xs" color={muted} fontWeight="700" textTransform="uppercase">Live signals</Text>
                        <Text fontSize="xs" fontWeight="700">{a.activeAlerts} alert{a.activeAlerts === 1 ? '' : 's'} · {a.normalized.anomalies_30d} anomal{a.normalized.anomalies_30d === 1 ? 'y' : 'ies'}</Text>
                        <Text fontSize="2xs" color={muted}>{a.sensorCount != null ? `${a.sensorCount} sensor${a.sensorCount === 1 ? '' : 's'}` : 'Sensors unknown'}</Text>
                    </Box>
                </SimpleGrid>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                <Box>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Why this health score</Text>
                    {a.drivers.length === 0 ? (
                        <HStack spacing={2}><Icon as={MdCheckCircle} color="green.400" /><Text fontSize="xs">No negative factors found.</Text></HStack>
                    ) : (
                        <VStack align="stretch" spacing={1.5}>
                            {a.drivers.map((d, i) => (
                                <Box key={i}>
                                    <Flex justify="space-between" fontSize="xs"><Text fontWeight="700">{d.label}</Text><Text fontFamily="mono" color={muted}>−{d.points}</Text></Flex>
                                    <Text fontSize="2xs" color={muted}>{d.detail}</Text>
                                </Box>
                            ))}
                        </VStack>
                    )}
                </Box>
                <Box>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Recommended actions</Text>
                    <VStack align="stretch" spacing={2}>
                        {a.actions.map((act, i) => (
                            <Flex key={i} gap={2} p={2} borderRadius="lg" bg={subtle} align="flex-start">
                                <Icon as={INTENT_ICON[act.intent] || MdOutlineAutoAwesome} color={`${PRIORITY_SCHEME[act.priority]}.400`} mt={0.5} />
                                <Box flex={1} minW={0}>
                                    <HStack spacing={1.5} wrap="wrap">
                                        <Text fontSize="xs" fontWeight="800">{act.label}</Text>
                                        <Badge colorScheme={PRIORITY_SCHEME[act.priority]} fontSize="9px">{act.priority}</Badge>
                                    </HStack>
                                    <Text fontSize="2xs" color={muted}>{act.explain}</Text>
                                </Box>
                                {onAction && act.intent !== 'VIEW' && (
                                    <Button size="xs" variant="outline" colorScheme="purple" flexShrink={0} onClick={() => onAction(act.intent, act)}>
                                        {INTENT_LABEL[act.intent] || 'Open'}
                                    </Button>
                                )}
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            </SimpleGrid>
        </Box>
    );
});
AssetHealthCard.displayName = 'AssetHealthCard';

// ─────────────────────────────────────────────────────────────────────────────
// Main helper
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {object} asset     API asset or form data
 * @param {'form'|'edit'|'view'} context
 * @param {'full'|'compact'} variant
 * @param {(fieldId, check) => void} onFix   jump to the field (form / edit)
 * @param {object} [analysis] precomputed analyzeAsset() result
 */
const AiAssetHelper = ({
    asset, context = 'view', variant = 'full', onFix, defaultOpen,
    sensors, alerts, workOrders, maintenanceLogs, insights, prediction, analysis: given,
    // --- UPDATE (v3)
    enableAsk, managerName,
    // --- END UPDATE
}) => {
    const analysis = useMemo(
        () => given || analyzeAsset(asset, context, { sensors, alerts, workOrders, maintenanceLogs, insights, prediction }),
        [given, asset, context, sensors, alerts, workOrders, maintenanceLogs, insights, prediction]
    );
    const [open, setOpen] = useState(defaultOpen ?? variant === 'full');
    const [expandedId, setExpandedId] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const shellBg = useColorModeValue('linear(135deg, teal.50, white 55%, purple.50)', 'linear(135deg, rgba(49,151,149,0.14), gray.800 55%, rgba(128,90,213,0.12))');
    const border = useColorModeValue('teal.100', 'whiteAlpha.200');
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardBorder = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const { score, grade, checks, models, insights: aiInsights, summary, familyLabel } = analysis;
    // --- UPDATE (v3): ask FlowSense about this asset
    const askEnabled = enableAsk ?? context === 'view';
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);
    const ask = useCallback(async () => {
        const q = question.trim();
        if (!q) return;
        setAsking(true); setAnswer(null);
        const missing = checks.filter(c => c.status !== 'ok').sort((a, b) => b.weight - a.weight).slice(0, 6).map(c => c.label);
        const ctx = {
            module: 'public_utilities_asset', asset_code: asset?.asset_code || null, name: asset?.name || null, type: asset?.type || null,
            status: asset?.status || null, constituency: asset?.constituency || null, managed_by: managerName || null,
            readiness_pct: score, summary, missing_data: missing, insights: (aiInsights || []).slice(0, 5).map(i => i.title),
        };
        try {
            const r = await askPublicUtilitiesAi(q, ctx);
            setAnswer({ text: r?.answer || r?.text || 'No answer returned.', ai: true });
        } catch (e) {
            setAnswer({ text: [summary, missing.length ? `Still missing: ${missing.join(', ')}.` : 'Nothing important is missing.'].filter(Boolean).join('\n'), ai: false });
        } finally { setAsking(false); }
    }, [question, checks, asset, managerName, score, summary, aiInsights]);
    const hasPuIdentity = !!(asset && ('asset_code' in asset || 'constituency' in asset || 'managed_by_org_id' in asset));
    // --- END UPDATE
    const outstanding = checks.filter(c => c.status !== 'ok').sort((a, b) => b.weight - a.weight);
    const done = checks.filter(c => c.status === 'ok');
    const visibleChecks = showAll ? [...outstanding, ...done] : outstanding;
    const compact = variant === 'compact';

    return (
        <Box bgGradient={shellBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={compact ? 3 : 4} role="region" aria-label="AI asset helper">
            <Flex align="center" gap={3}>
                <ReadinessRing score={score} grade={grade} size={compact ? '48px' : '60px'} />
                <Box flex={1} minW={0}>
                    <HStack spacing={2} wrap="wrap">
                        <Icon as={MdPsychology} color="purple.400" />
                        <Text fontWeight="800" fontSize={compact ? 'sm' : 'md'}>AI Asset Helper</Text>
                        <Badge colorScheme={grade.color} borderRadius="full">{grade.label}</Badge>
                        <Badge colorScheme="teal" variant="outline" borderRadius="full" fontSize="9px">{familyLabel}</Badge>
                    </HStack>
                    <Text fontSize="xs" color={muted} mt={0.5} noOfLines={compact && !open ? 1 : undefined}>{summary}</Text>
                </Box>
                <IconButton size="sm" variant="ghost" aria-label={open ? 'Collapse AI helper' : 'Expand AI helper'}
                    icon={<Icon as={open ? MdExpandLess : MdExpandMore} />} onClick={() => setOpen(o => !o)} />
            </Flex>

            {/* --- UPDATE (v3): Public Utilities identity — code, constituency, who manages it */}
            {hasPuIdentity && (
                <HStack spacing={1.5} mt={2} wrap="wrap" data-testid="ai-helper-identity">
                    {asset.asset_code && <Badge variant="outline" fontFamily="mono" fontSize="10px">{asset.asset_code}</Badge>}
                    <Badge colorScheme={asset.constituency ? 'teal' : 'orange'} variant="subtle" fontSize="10px">{asset.constituency || 'Not in a constituency'}</Badge>
                    <Badge colorScheme={asset.managed_by_org_id != null ? 'purple' : 'orange'} variant="subtle" fontSize="10px">
                        {asset.managed_by_org_id != null ? (managerName || 'Managed') : 'No manager — work cannot be routed'}
                    </Badge>
                </HStack>
            )}
            {/* --- END UPDATE */}

            {/* Model strip — always visible */}
            <HStack spacing={1.5} mt={3} wrap="wrap">
                {models.map(m => (
                    <Tooltip key={m.key} hasArrow label={
                        <Box fontSize="xs">
                            <Text fontWeight="800">{m.label} — {MODEL_STATE[m.state].label}</Text>
                            <Text mt={1}>{m.explain}</Text>
                            {m.missing.length > 0 && <Text mt={1}>Missing: {m.missing.map(c => c.label.toLowerCase()).join(', ')}</Text>}
                        </Box>
                    }>
                        <Badge colorScheme={MODEL_STATE[m.state].scheme} variant="subtle" borderRadius="full" px={2} py={0.5}
                            display="flex" alignItems="center" gap={1} cursor="help" fontSize="10px">
                            <Icon as={MODEL_ICON[m.key]} boxSize={3} />{m.short}
                        </Badge>
                    </Tooltip>
                ))}
            </HStack>

            <Collapse in={open} animateOpacity>
                <Box mt={3}>
                    {aiInsights.length > 0 && (
                        <VStack align="stretch" spacing={2} mb={3}>
                            {aiInsights.slice(0, compact ? 3 : 6).map((ins, i) => {
                                const st = INSIGHT_STYLE[ins.severity] || INSIGHT_STYLE.info;
                                return (
                                    <Flex key={i} gap={2} p={2.5} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg">
                                        <Icon as={st.icon} color={st.color} mt={0.5} flexShrink={0} />
                                        <Box>
                                            <HStack spacing={1.5}>
                                                <Text fontSize="xs" fontWeight="800">{ins.title}</Text>
                                                {ins.fromModel && <Badge colorScheme="purple" fontSize="9px">ML</Badge>}
                                            </HStack>
                                            {ins.text && <Text fontSize="xs" color={muted}>{ins.text}</Text>}
                                        </Box>
                                    </Flex>
                                );
                            })}
                        </VStack>
                    )}

                    <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={2}>
                        <Flex justify="space-between" align="center" px={2} pb={1}>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted}>
                                {outstanding.length ? `What FlowSense still needs (${outstanding.length})` : 'All information complete'}
                            </Text>
                            {done.length > 0 && (
                                <Button size="xs" variant="link" colorScheme="purple" onClick={() => setShowAll(s => !s)}>
                                    {showAll ? 'Hide complete' : `Show complete (${done.length})`}
                                </Button>
                            )}
                        </Flex>
                        {visibleChecks.length === 0 ? (
                            <HStack px={2} py={2} spacing={2}><Icon as={MdCheckCircle} color="green.400" /><Text fontSize="xs">Nothing missing — predictions use complete data.</Text></HStack>
                        ) : (
                            <VStack align="stretch" spacing={0} maxH={compact ? '220px' : '360px'} overflowY="auto">
                                {visibleChecks.map(c => (
                                    <CheckRow key={c.id} c={c} onFix={onFix} expanded={expandedId === c.id}
                                        onToggle={() => setExpandedId(id => (id === c.id ? null : c.id))} />
                                ))}
                            </VStack>
                        )}
                    </Box>
                    {/* --- UPDATE (v3): Ask FlowSense about this asset */}
                    {askEnabled && (
                        <Box mt={3}>
                            <InputGroup size="sm">
                                <Input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                                    placeholder="Ask FlowSense about this asset…" borderRadius="full" bg={cardBg} aria-label="Ask FlowSense about this asset" />
                                <InputRightElement>
                                    {asking ? <Spinner size="xs" /> : <IconButton size="xs" variant="ghost" aria-label="Ask" icon={<Icon as={MdSend} />} onClick={ask} isDisabled={!question.trim()} />}
                                </InputRightElement>
                            </InputGroup>
                            {answer && (
                                <Box mt={2} p={2.5} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg">
                                    <Text fontSize="2xs" fontWeight="800" color={muted} mb={1}>{answer.ai ? 'FlowSense AI' : 'FlowSense AI is offline — the helper says'}</Text>
                                    <Text fontSize="xs" whiteSpace="pre-wrap">{answer.text}</Text>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* --- END UPDATE */}
                </Box>
            </Collapse>
        </Box>
    );
};

export default memo(AiAssetHelper);

// ─────────────────────────────────────────────────────────────────────────────
// Table cell — health + readiness, popover with the compact helper
// ─────────────────────────────────────────────────────────────────────────────
export const AssetAiCell = memo(({ asset, onEdit, sensors }) => {
    const analysis = useMemo(() => (sensors ? analyzeAsset(asset, 'view', { sensors }) : getAssetAnalysis(asset)), [asset, sensors]);
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const popBg = useColorModeValue('white', 'gray.800');
    return (
        <Popover placement="left" isLazy>
            <PopoverTrigger>
                <VStack as="button" type="button" align="start" spacing={1} onClick={(e) => e.stopPropagation()}
                    aria-label={`AI health ${analysis.health}, data readiness ${analysis.score}% for ${asset.name || asset.type || 'asset'}`}
                    bg="transparent" border="none" p={0} textAlign="left">
                    <HStack spacing={1.5}>
                        <Icon as={MdHealthAndSafety} color={`${analysis.healthLevel.color}.400`} boxSize={3.5} />
                        <Badge colorScheme={analysis.healthLevel.color} variant="subtle" borderRadius="full" fontSize="10px">
                            {analysis.health} · {analysis.healthLevel.label}
                        </Badge>
                    </HStack>
                    <HStack spacing={1} w="90px">
                        <Progress value={analysis.score} size="xs" flex={1} borderRadius="full" bg={track} colorScheme={analysis.grade.color} />
                        <Text fontSize="9px" color={muted} fontFamily="mono">{analysis.score}%</Text>
                    </HStack>
                </VStack>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w="380px" bg={popBg} borderRadius="xl" boxShadow="2xl" onClick={(e) => e.stopPropagation()}>
                    <PopoverArrow bg={popBg} />
                    <PopoverBody p={2}>
                        <AiAssetHelper asset={asset} analysis={analysis} context="view" variant="compact" defaultOpen
                            onFix={onEdit ? () => onEdit(asset) : undefined} />
                        {analysis.drivers.length > 0 && (
                            <Box px={2} pt={2}>
                                <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={muted}>Health drivers</Text>
                                {analysis.drivers.slice(0, 3).map((d, i) => (
                                    <Flex key={i} justify="space-between" fontSize="xs"><Text>{d.label}</Text><Text fontFamily="mono" color={muted}>−{d.points}</Text></Flex>
                                ))}
                            </Box>
                        )}
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
});
AssetAiCell.displayName = 'AssetAiCell';

// ─────────────────────────────────────────────────────────────────────────────
// Predictive Maintenance "Why?" explainer
// ─────────────────────────────────────────────────────────────────────────────
export const PredictionExplainer = memo(({ prediction }) => {
    const bg = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const actionBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.15)');
    const e = useMemo(() => explainPrediction(prediction), [prediction]);
    return (
        <Popover trigger="click" placement="left" isLazy>
            <PopoverTrigger>
                <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<Icon as={MdOutlineAutoAwesome} />}
                    aria-label={`Explain prediction for ${prediction.assetName || 'asset'}`}>
                    Why?
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w="320px" bg={bg} borderRadius="xl" boxShadow="xl">
                    <PopoverArrow bg={bg} />
                    <PopoverBody p={3}>
                        <HStack spacing={1.5} mb={2}>
                            <Icon as={MdPsychology} color="purple.400" />
                            <Text fontWeight="800" fontSize="sm" noOfLines={1}>{prediction.assetName || 'Asset'}</Text>
                            <Badge colorScheme={e.level.color}>{e.level.label}</Badge>
                        </HStack>
                        <VStack align="stretch" spacing={1.5}>
                            {e.points.map((p, i) => <Text key={i} fontSize="xs" color={muted}>• {p}</Text>)}
                        </VStack>
                        <Box mt={2} p={2} borderRadius="md" bg={actionBg}>
                            <Text fontSize="xs" fontWeight="800">Recommended: {e.action}</Text>
                        </Box>
                        <Text fontSize="2xs" color={muted} mt={2}>
                            {e.familyLabel}{e.usesSensors ? ' — live sensors improve this prediction.' : '.'} Open the asset's AI helper to see which data would make it more accurate.
                        </Text>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
});
PredictionExplainer.displayName = 'PredictionExplainer';